// ReceiptPrinterEncoder implementation
import ReceiptPrinterEncoder from "@point-of-sale/receipt-printer-encoder";
import {
  getBillColumnLayout,
  is58mmPrintConfig,
  normalizePrinterPrintConfig,
  resolvePrintColumns,
  shouldUseDoubleSizeHeader,
} from "./thermalPrinterConfig.js";

class ReceiptPrinterEncoderWrapper {
  constructor() {}

  createEncoder(config) {
    const printConfig = normalizePrinterPrintConfig(config);
    return new ReceiptPrinterEncoder({
      columns: resolvePrintColumns(printConfig),
    });
  }

  /** Smaller Font B on 58mm; keeps column layout unchanged. */
  apply58mmTypography(encoder, printConfig) {
    if (!is58mmPrintConfig(printConfig)) return;
    encoder.font("b");
  }

  printSeparator(encoder, compact58 = false) {
    if (compact58) {
      encoder.line("-".repeat(encoder.columns)).newline();
      return;
    }
    const halfLine = Math.max(1, Math.floor(encoder.columns / 2));
    encoder.width(2).line("-".repeat(halfLine)).width(1).newline();
  }

  printBill(billData, config) {
    const printConfig = normalizePrinterPrintConfig(config);
    const encoder = this.createEncoder(printConfig);
    const width = resolvePrintColumns(printConfig);
    const compact58 = is58mmPrintConfig(printConfig);
    const hotelName = billData.hotelData?.name || "";
    const useDoubleHeader =
      !compact58 && shouldUseDoubleSizeHeader(width, hotelName);

    encoder.initialize();
    this.apply58mmTypography(encoder, printConfig);
    encoder.newline();

    encoder.align("center");
    if (useDoubleHeader) {
      encoder.size(2).bold(true).text(hotelName).bold(false).size(1);
    } else {
      encoder.bold(true).text(hotelName).bold(false);
    }

    encoder.newline(useDoubleHeader ? 1 : 2);
    encoder.bold(true).text(billData.hotelData.address).bold(false);
    encoder.newline();
    encoder.size(1);

    if (billData.hotelData.contact) {
      encoder.text(billData.hotelData.contact).newline();
    }

    if (
      billData.GST &&
      billData.GST.gstNumber &&
      billData.GST.gstNumber !== "NA" &&
      billData.GST.gstNumber !== ""
    ) {
      encoder
        .bold(true)
        .text("GSTN:")
        .bold(false)
        .text(` ${billData.GST.gstNumber}`)
        .newline();
    }

    if (
      billData.GST.fassi_no &&
      billData.GST.fassi_no !== "NA" &&
      billData.GST.fassi_no !== "" &&
      billData.GST.fassi_no !== 0
    ) {
      encoder
        .bold(true)
        .text("FSSAI:")
        .bold(false)
        .text(` ${billData.GST.fassi_no}`)
        .newline();
    }

    this.printSeparator(encoder, compact58);
    encoder.align("left");

    let normalBillNo = billData.billNo.toString();
    let highlightedBillNo = "";

    if (billData.hightLightOrderIDLastNDigits > 0) {
      normalBillNo = normalBillNo.slice(
        0,
        -billData.hightLightOrderIDLastNDigits
      );
      highlightedBillNo = `- ${billData.billNo
        .toString()
        .slice(-billData.hightLightOrderIDLastNDigits)}`;
    }

    // Add bill info in two columns
    encoder
      .bold(true)
      .text("Bill No:")
      .bold(false)
      .text(` ${normalBillNo}`)
      .bold(true)
      .text(highlightedBillNo)
      .bold(false)
      .newline();
    encoder
      .bold(true)
      .text("Date:")
      .bold(false)
      .text(` ${billData.formattedDateTime}`)
      .newline();
    encoder
      .bold(true)
      .text("Table:")
      .bold(false)
      .text(` ${billData.roomData.table} Room: ${billData.roomData.room}`)
      .newline();

    this.printSeparator(encoder, compact58);

    const { qtyWidth, priceWidth, amountWidth, gaps } =
      getBillColumnLayout(width);
    const itemWidth = Math.max(8, width - (qtyWidth + priceWidth + amountWidth + gaps));

    encoder.bold(true);
    encoder.table(
      [
        { width: itemWidth, align: "left" },
        { width: 1, align: "left" },
        { width: qtyWidth, align: "right" },
        { width: 1, align: "left" },
        { width: priceWidth, align: "right" },
        { width: 1, align: "left" },
        { width: amountWidth, align: "right" },
      ],
      [["Item", "", "Qty", "", "Price", "", "Amt"]]
    );
    encoder.bold(false);

    this.printSeparator(encoder, compact58);

    // Add items
    for (const item of billData.validItems) {
      const addonsList = item.addOn || item.addOns || item.addons || [];
      const addonSum = addonsList.reduce((a, b) => a + Number(b.price || 0), 0);
      const displayPrice = Number(item.price);
      const discountAmount = Number(item.discountAmount) || 0;
      const effectivePrice = displayPrice - discountAmount;
      const total = item.quantity * (effectivePrice + addonSum);

      encoder.table(
        [
          { width: itemWidth, align: "left" },
          { width: 1, align: "left" },
          { width: qtyWidth, align: "right" },
          { width: 1, align: "left" },
          { width: priceWidth, align: "right" },
          { width: 1, align: "left" },
          { width: amountWidth, align: "right" },
        ],
        [
          [
            (encoder) =>
              encoder.text(
                `${item.name} ${item.isVariant ? `- ${item.variantName}` : ""}`
              ),
            "",
            item.quantity.toString(),
            "",
            effectivePrice.toFixed(2),
            "",
            total.toFixed(2),
          ],
        ]
      );

      if (addonsList.length > 0) {
        addonsList.forEach((a) => {
          encoder.table(
            [
              { width: itemWidth, align: "left" },
              { width: 1, align: "left" },
              { width: qtyWidth, align: "right" },
              { width: 1, align: "left" },
              { width: priceWidth, align: "right" },
              { width: 1, align: "left" },
              { width: amountWidth, align: "right" },
            ],
            [
              [
                (encoder) => encoder.text(`  * ${a.name}`),
                "",
                "",
                "",
                Number(a.price || 0).toFixed(2),
                "",
                "",
              ]
            ]
          );
        });
      }
    }

    this.printSeparator(encoder, compact58);

    const summaryLabelWidth = Math.floor(width * 0.6);
    const summaryValueWidth = width - summaryLabelWidth;

    // Add transaction summary
    const txnLogs = billData.billTxnLogs;
    encoder.table(
      [
        { width: summaryLabelWidth, align: "right" },
        { width: summaryValueWidth, align: "right" },
      ],
      [
        [
          (encode) => encode.bold(true).text("Sub Total:").bold(false),
          txnLogs.subTotal.toFixed(2),
        ],
      ]
    );

    if (txnLogs.discountFlat > 0) {
      encoder.table(
        [
          { width: summaryLabelWidth, align: "right" },
          { width: summaryValueWidth, align: "right" },
        ],
        [
          [
            (encode) => encode.bold(true).text("Flat Discount:").bold(false),
            txnLogs.discountFlat.toFixed(2),
          ],
        ]
      );
    }

    if (txnLogs.discountPercent > 0) {
      const discountAmount = (txnLogs.discountPercent * txnLogs.subTotal) / 100;
      encoder.table(
        [
          { width: summaryLabelWidth, align: "right" },
          { width: summaryValueWidth, align: "right" },
        ],
        [
          [
            (encode) =>
              encode
                .bold(true)
                .text(`Discount (${txnLogs.discountPercent}%):`)
                .bold(false),
            discountAmount.toFixed(2),
          ],
        ]
      );
    }

    if (billData.discount) {
      encoder.table(
        [
          { width: summaryLabelWidth, align: "right" },
          { width: summaryValueWidth, align: "right" },
        ],
        [
          [
            (encode) => encode.bold(true).text("Discount:").bold(false),
            billData.discount.toFixed(2),
          ],
        ]
      );
    }

    if (txnLogs.gst) {
      encoder.table(
        [
          { width: summaryLabelWidth, align: "right" },
          { width: summaryValueWidth, align: "right" },
        ],
        [
          [
            (encode) =>
              encode
                .bold(true)
                .text(`CGST (${txnLogs.cgstPercent}%):`)
                .bold(false),
            txnLogs.cgst.toFixed(2),
          ],
          [
            (encode) =>
              encode
                .bold(true)
                .text(`SGST (${txnLogs.sgstPercent}%):`)
                .bold(false),
            txnLogs.sgst.toFixed(2),
          ],
        ]
      );
    }

    // Add separator
    encoder.rule({ style: "double", width: width });

    encoder.table(
      [
        { width: summaryLabelWidth, align: "right" },
        { width: summaryValueWidth, align: "right" },
      ],
      [
        [
          (encode) =>
            encode
              .height(useDoubleHeader ? 2 : 1)
              .bold(true)
              .text("GRAND TOTAL:")
              .bold(false)
              .height(1),
          (encode) =>
            encode
              .height(useDoubleHeader ? 2 : 1)
              .bold(true)
              .text(txnLogs.total.toFixed(2))
              .bold(false)
              .height(1),
        ],
      ]
    );

    encoder.newline(1);

    if (txnLogs.tips > 0) {
      encoder.table(
        [
          { width: summaryLabelWidth, align: "right" },
          { width: summaryValueWidth, align: "right" },
        ],
        [
          [
            (encode) => encode.bold(true).text("Tips:").bold(false),
            txnLogs.tips.toFixed(2),
          ],
        ]
      );
    }

    // Add footer
    if (txnLogs.gst && !txnLogs.gstExcluded && txnLogs.showGSTFooter) {
      encoder.align("center").text("GST Already Included in Item Price");
    }

    encoder.align("center").text("Powered by QRapid");

    // Add QR Code if provided
    if (billData.qrData) {
      encoder.align("center").qrcode(billData.qrData, {
        model: 2,
        size: compact58 ? 5 : width <= 32 ? 6 : 8,
        errorlevel: "l",
      });
    }

    encoder.align("center").text("");
    encoder.align("center").text("");
    encoder.align("center").text("");
    // Cut paper

    return encoder.encode();
  }

  printKOT(kotData, config) {
    const printConfig = normalizePrinterPrintConfig(config);
    const encoder = this.createEncoder(printConfig);
    const width = resolvePrintColumns(printConfig);
    const compact58 = is58mmPrintConfig(printConfig);
    const kotTitle = kotData.type || "KOT";
    const useDoubleHeader =
      !compact58 && shouldUseDoubleSizeHeader(width, kotTitle);

    encoder.initialize();
    this.apply58mmTypography(encoder, printConfig);
    encoder.newline();

    encoder.align("center");
    if (useDoubleHeader) {
      encoder.size(2).bold(true).text(kotTitle).bold(false).size(1);
    } else {
      encoder.bold(true).text(kotTitle).bold(false);
    }
    encoder.newline(useDoubleHeader ? 1 : 2);

    encoder.align("left");

    let normalBillNo = kotData.billNo.toString();
    let highlightedBillNo = "";

    if (kotData.hightLightOrderIDLastNDigits > 0) {
      normalBillNo = normalBillNo.slice(
        0,
        -kotData.hightLightOrderIDLastNDigits
      );
      highlightedBillNo = `- ${kotData.billNo
        .toString()
        .slice(-kotData.hightLightOrderIDLastNDigits)}`;
    }

    encoder
      .bold(true)
      .text("Bill No:")
      .bold(false)
      .text(` ${normalBillNo}`)
      .bold(true)
      .text(highlightedBillNo)
      .bold(false)
      .newline();
    encoder
      .bold(true)
      .text("Date:")
      .bold(false)
      .text(` ${kotData.formattedDateTime}`)
      .newline();

    encoder
      .bold(true)
      .text("Table:")
      .bold(false)
      .text(` ${kotData.roomData.table} Room: ${kotData.roomData.room}`)
      .newline();

    // Add separator
    encoder.rule({ style: "single", width: width });

    // Add customer section if exists
    if (kotData.customer?.name && kotData.customer.name !== "NA") {
      if (kotData.type === "Parcel KOT") {
        encoder
          .bold(true)
          .text("Customer:")
          .bold(false)
          .text(` ${kotData.customer.name}`)
          .newline();

        if (kotData.customer.phone && kotData.customer.phone !== "NA") {
          encoder
            .bold(true)
            .text("Ph:")
            .bold(false)
            .text(` ${kotData.customer.phone}`)
            .newline();
        }

        if (kotData.customer.address && kotData.customer.address !== "NA") {
          encoder
            .bold(true)
            .text("Add:")
            .bold(false)
            .text(` ${kotData.customer.address}`)
            .newline();
        }
      } else {
        encoder
          .bold(true)
          .text("Customer:")
          .bold(false)
          .text(` ${kotData.customer.name}`)
          .newline();
      }

      encoder.rule({ style: "single", width: width });
    }

    // Add items header
    encoder.table(
      [
        { width: width - 6, align: "left" },
        { width: 6, align: "right" },
      ],
      [["Item", "Qty"]]
    );

    encoder.rule({ style: "single", width: width });

    // Add canceled items if any
    if (kotData.cancelItems && kotData.cancelItems.length > 0) {
      kotData.cancelItems.forEach((item) => {
        const itemName = item.isVariant
          ? `${item.name} - ${item.variantName}`
          : item.name;
        encoder.table(
          [
            { width: width - 6, align: "left" },
            { width: 6, align: "right" },
          ],
          [
            [
              (encoder) =>
                encoder
                  .bold(true)
                  .invert(true)
                  .text(`${itemName}`)
                  .invert(false)
                  .bold(false),
              (encoder) =>
                encoder.bold(true).text(item.quantity.toString()).bold(false),
            ],
          ]
        );

        const addonsList = item.addOn || item.addOns || item.addons || [];
        if (addonsList.length > 0) {
          addonsList.forEach((a) => {
            encoder.text(`  * ${a.name}`).newline();
          });
        }
      });
    }

    // Add active items
    kotData.cartItems
      .filter((item) => !item.canceled)
      .forEach((item) => {
        const itemName = item.isVariant
          ? `${item.name} - ${item.variantName}`
          : item.name;
        encoder.table(
          [
            { width: width - 4, align: "left" },
            { width: 4, align: "right" },
          ],
          [
            [
              (encoder) => encoder.bold(true).text(itemName).bold(false),
              (encoder) =>
                encoder.bold(true).text(item.quantity.toString()).bold(false),
            ],
          ]
        );

        const addonsList = item.addOn || item.addOns || item.addons || [];
        if (addonsList.length > 0) {
          addonsList.forEach((a) => {
            encoder.text(`  * ${a.name}`).newline();
          });
        }

        if (item.comment) {
          encoder.text(`→ ${item.comment}`).newline();
        }
      });

    encoder.rule({ style: "single", width: width }).newline();

    // Add comment if exists
    if (kotData.comment) {
      encoder
        .bold(true)
        .text("Comment:")
        .bold(false)
        .text(` ${kotData.comment}`)
        .newline()
        .rule({ style: "single", width: width })
        .newline();
    }

    // Add footer
    encoder.align("center").text("Powered by QRapid");
    encoder.align("center").text("");
    encoder.align("center").text("");
    encoder.align("center").text("");

    return encoder.encode();
  }

  WidthPrint(config) {
    const printConfig = normalizePrinterPrintConfig(config);
    const encoder = this.createEncoder(printConfig);
    encoder.initialize();
    // Print 'abcd...zabcd...z' two times
    const alphabet = "abcdefghijklmnopqrstuvwxyz";
    const text = alphabet + alphabet;
    return encoder.text(text).newline().text(text).newline().encode();
  }
}

export default new ReceiptPrinterEncoderWrapper();
